/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import React, {CSSProperties, ReactElement} from 'react';

import {AccessKey, ContextShape, DefaultVATValue, SalesDashboardProduct, VATValue} from './interfaces';

import {DataTableFilterMetaData, DataTableOperatorFilterMetaData} from 'primereact/datatable';

import {FilterMatchMode, FilterOperator} from 'primereact/api';

import {ColumnFilterMatchModeOptions} from 'primereact/column';

import {AwesomeIcon} from '@dvrd/dvr-controls';

import InfoIcon from '../components/icon/infoIcon';

import CountryFlag from '../components/misc/countryFlag';

import {onNavigate} from './utils';

import {userHasRight} from './accessUtil';

import classNames from 'classnames';

import {CountryVATValues, translateVatValue, VATValuesMatch} from './vatUtil';

import {ProductDimensions} from './types';

import {LVBSize, SalesCategory, SalesColumn} from './enums';

import i18n from '../i18n';

export type ColumnConfig = {
    label: string;
    field: string;
    style?: CSSProperties;
    styleRowOnly?: boolean;
    body?: (product: SalesDashboardProduct) => ReactElement | string | number;
    functionBody?: (
        props: any,
        context?: ContextShape
    ) => (product: SalesDashboardProduct) => ReactElement | string | number;
    sortable?: true;
    filter?: true;
    frozen?: true;
    editorType?: 'decimal' | 'currency';
    matchModes?: Array<ColumnFilterMatchModeOptions>;
    dataType?: string;
};

export type ColumnGroupConfig = {
    label: string;
    columns: Array<ColumnConfig>;
    frozen?: true;
};

export const getSalesDashboardColumns = (): Array<ColumnGroupConfig> => [
    {
        label: '',
        frozen: true,
        columns: [
            {
                label: '',
                field: 'image_url',
                style: {padding: '0 .5rem', width: 'unset'},
                body: renderImage,
                frozen: true
            },
            {
                label: i18n.t('util.salesDashboard.col.name', 'Naam'),
                field: 'title',
                style: {minWidth: '15rem', maxWidth: '15rem'},
                body: renderTitle,
                filter: true,
                sortable: true,
                frozen: true
            }
        ]
    },
    {
        label: i18n.t('util.salesDashboard.group.revenue', 'Omzet'),
        columns: [
            {
                label: i18n.t('util.salesDashboard.col.quantity', 'Aantal verkocht'),
                field: 'quantity',
                filter: true,
                sortable: true,
                dataType: 'numeric',
                body: renderNumber('quantity', true)
            },
            {
                label: i18n.t('util.salesDashboard.col.numOrders', 'Aantal orders'),
                field: 'num_orders',
                filter: true,
                sortable: true,
                dataType: 'numeric',
                body: renderNumber('num_orders', true)
            },
            {
                label: i18n.t('util.salesDashboard.col.revenue', 'Omzet'),
                field: 'revenue',
                body: renderCurrency('revenue'),
                filter: true,
                sortable: true,
                dataType: 'numeric'
            },
            {
                label: i18n.t('util.salesDashboard.col.revenueIncl', 'Omzet incl. BTW'),
                field: 'revenue_incl',
                filter: true,
                sortable: true,
                dataType: 'numeric',
                body: renderCurrency('revenue_incl')
            },
            {
                label: i18n.t('util.salesDashboard.col.avgPrice', 'Verkoopprijs bol'),
                field: 'avg_price',
                filter: true,
                sortable: true,
                body: renderWithInfo(
                    'avg_price',
                    i18n.t('util.salesDashboard.info.avgPrice', 'Gemiddelde prijs van alle orders van dit product'),
                    true
                ),
                dataType: 'numeric',
                style: {padding: 0, overflow: 'visible'},
                styleRowOnly: true
            },
            {
                label: i18n.t('util.salesDashboard.col.avgPriceIncl', 'Verkoopprijs bol incl. BTW'),
                field: 'avg_price_incl',
                filter: true,
                sortable: true,
                body: renderWithInfo(
                    'avg_price_incl',
                    i18n.t(
                        'util.salesDashboard.info.avgPriceIncl',
                        'Gemiddelde prijs van alle orders van dit product, inclusief BTW'
                    ),
                    true
                ),
                dataType: 'numeric',
                style: {padding: 0, overflow: 'visible'},
                styleRowOnly: true
            },
            {
                label: i18n.t('util.salesDashboard.col.vatPercentage', 'BTW percentage'),
                field: 'vat_percentage',
                body: renderVatPercentages,
                style: {padding: 0},
                styleRowOnly: true
            }
        ]
    },
    {
        label: i18n.t('util.salesDashboard.group.returns', 'Retouren'),
        columns: [
            {
                label: i18n.t('util.salesDashboard.col.returnedQuantity', 'Aantal retour'),
                field: 'returned_quantity',
                filter: true,
                sortable: true,
                dataType: 'numeric',
                body: renderNumber('returned_quantity', true)
            }
        ]
    },
    {
        label: i18n.t('util.salesDashboard.group.costs', 'Kosten'),
        columns: [
            {
                label: i18n.t('util.salesDashboard.col.costs', 'Productkosten'),
                field: 'costs',
                filter: true,
                sortable: true,
                functionBody: (props: any, context: ContextShape) => {
                    return function (rowData: SalesDashboardProduct) {
                        const rowValue = Number(rowData.costs ?? '');
                        let value = new Intl.NumberFormat(getCurrencyLocale(), {
                            style: 'currency',
                            currency: 'EUR'
                        }).format(rowValue);
                        if (rowValue === 0 && !rowData.has_product_costs) value = '\u20ac -';
                        const canEdit = userHasRight(context, AccessKey.SALES_DASHBOARD_COST_PRICES);
                        return (
                            <div
                                className={classNames('editable-currency', !canEdit && 'cursor-default')}
                                onClick={canEdit ? props.onConfigureCosts(rowData) : undefined}
                            >
                                {value}
                                {canEdit && <AwesomeIcon name='cog' className='edit-icon' />}
                            </div>
                        );
                    };
                },
                dataType: 'numeric',
                editorType: 'currency',
                style: {padding: 0},
                styleRowOnly: true
            },
            {
                label: i18n.t('util.salesDashboard.col.commissionPercentage', 'Commissie %'),
                field: 'commission_percentage',
                sortable: true,
                filter: true,
                body: renderPercentage('commission_percentage'),
                dataType: 'numeric',
                style: {padding: 0},
                styleRowOnly: true
            },
            {
                label: i18n.t('util.salesDashboard.col.avgCommission', 'Gemiddelde commissie'),
                field: 'avg_commission',
                filter: true,
                sortable: true,
                body: renderCurrency('avg_commission'),
                dataType: 'numeric'
            },
            {
                label: i18n.t('util.salesDashboard.col.commission', 'Totale commissie'),
                field: 'commission',
                filter: true,
                sortable: true,
                body: renderCurrency('commission'),
                dataType: 'numeric'
            }
        ]
    },
    {
        label: i18n.t('util.salesDashboard.group.profit', 'Winst'),
        columns: [
            {
                label: i18n.t('util.salesDashboard.col.profit', 'Winst'),
                field: 'profit',
                filter: true,
                sortable: true,
                editorType: 'currency',
                body: renderCurrency(
                    'profit',
                    false,
                    true,
                    i18n.t(
                        'util.salesDashboard.info.profitNeedsCost',
                        'De winst kan pas berekend worden als de kostprijs ingevuld is.'
                    )
                ),
                dataType: 'numeric',
                style: {padding: 0, overflow: 'visible'},
                styleRowOnly: true
            },
            {
                label: i18n.t('util.salesDashboard.col.profitPerProduct', 'Winst per product'),
                field: 'profit_per_product',
                filter: true,
                sortable: true,
                editorType: 'currency',
                body: renderCurrency(
                    'profit_per_product',
                    false,
                    true,
                    i18n.t(
                        'util.salesDashboard.info.profitNeedsCost',
                        'De winst kan pas berekend worden als de kostprijs ingevuld is.'
                    )
                ),
                dataType: 'numeric',
                style: {padding: 0, overflow: 'visible'},
                styleRowOnly: true
            },
            {
                label: i18n.t('util.salesDashboard.col.margin', 'Marge %'),
                field: 'margin',
                filter: true,
                sortable: true,
                editorType: 'decimal',
                body: renderPercentage(
                    'margin',
                    true,
                    true,
                    i18n.t(
                        'util.salesDashboard.info.marginNeedsCost',
                        'De winstmarge kan pas berekend worden als de kostprijs ingevuld is.'
                    )
                ),
                dataType: 'numeric',
                style: {padding: 0, overflow: 'visible'},
                styleRowOnly: true
            }
        ]
    }
];

export function getSelectableColumnGroups(groups: Array<ColumnGroupConfig>): Array<ColumnGroupConfig> {
    return groups.filter((group: ColumnGroupConfig) => group.label.length > 0);
}

export function getSelectableColumns(groups: Array<ColumnGroupConfig>): Array<ColumnConfig> {
    return getSelectableColumnGroups(groups).reduce(
        (prev: Array<ColumnConfig>, current: ColumnGroupConfig) => prev.concat(current.columns),
        []
    );
}

function renderImage(product: SalesDashboardProduct) {
    return <img className='image-container' src={product.image_url ?? ''} alt='' />;
}

let currencyLocaleCache: string | null = null;
let bolLocalePathCache: string | null = null;
i18n.on('languageChanged', () => {
    currencyLocaleCache = null;
    bolLocalePathCache = null;
});

function getCurrencyLocale(): string {
    if (currencyLocaleCache === null) {
        currencyLocaleCache = (i18n.resolvedLanguage ?? i18n.language) === 'en' ? 'en-US' : 'nl-NL';
    }
    return currencyLocaleCache;
}

function getBolLocalePath(): string {
    if (bolLocalePathCache === null) {
        bolLocalePathCache = (i18n.resolvedLanguage ?? i18n.language) === 'en' ? 'nl/en' : 'nl/nl';
    }
    return bolLocalePathCache;
}

function renderTitle(product: SalesDashboardProduct) {
    const link = `https://www.bol.com/${getBolLocalePath()}/p/${encodeURIComponent(product.title)}/${encodeURIComponent(
        product.global_id ?? ''
    )}`;
    return (
        <div className='title-container'>
            <span title={product.title}>
                {product.title}
                <br />
            </span>
            <span className='ean-style'>EAN: {product.ean}</span>
            {!!product.global_id && (
                <a href={link} target='_blank' className='product-link'>
                    <AwesomeIcon name='arrow-up-right-from-square' />
                </a>
            )}
        </div>
    );
}

function renderCurrency(
    field: keyof SalesDashboardProduct,
    editable: boolean = false,
    dashIfZero: boolean = false,
    infoIfZero?: string
) {
    return function (rowData: SalesDashboardProduct) {
        const rowValue = Number(rowData[field] ?? '');
        let value = new Intl.NumberFormat(getCurrencyLocale(), {
            style: 'currency',
            currency: 'EUR'
        }).format(rowValue);
        const showInfo = !!infoIfZero && !rowValue && !rowData.has_product_costs;
        if (rowValue === 0 && dashIfZero && !rowData.has_product_costs) value = '\u20ac -';
        if (editable)
            return (
                <div className='editable-currency'>
                    {value}
                    <AwesomeIcon name='edit' className='edit-icon' />
                </div>
            );
        else if (infoIfZero)
            return (
                <div className='cell-with-info'>
                    <span>{value}</span>
                    {showInfo && (
                        <div className='cell-info-container'>
                            <InfoIcon />
                            <p className='info-message'>{infoIfZero}</p>
                        </div>
                    )}
                </div>
            );
        return value;
    };
}

function renderWithInfo(field: keyof SalesDashboardProduct, info: string, isCurrency: boolean = false) {
    return function (rowData: SalesDashboardProduct) {
        const rowValue = rowData[field] ?? '';
        let value: string | number = rowValue as string | number;
        if (isCurrency) {
            value = new Intl.NumberFormat(getCurrencyLocale(), {
                style: 'currency',
                currency: 'EUR'
            }).format(Number(rowValue));
        }
        return (
            <div className='cell-with-info'>
                <span>{value}</span>
                <div className='cell-info-container'>
                    <InfoIcon />
                    <p className='info-message'>{info}</p>
                </div>
            </div>
        );
    };
}

function renderVatPercentages(rowData: SalesDashboardProduct) {
    const countryValues = new CountryVATValues<DefaultVATValue>(rowData.vat_percentage);
    const countryCodes: Array<string> = Array.from(countryValues.countryData.keys());
    const vatValues: Array<DefaultVATValue> = Array.from(countryValues.countryData.values());
    const uniquePercentages: Array<VATValue> = [];

    mainLoop: for (const vatValue of vatValues) {
        for (const _vatValue of uniquePercentages) {
            if (VATValuesMatch(vatValue, _vatValue)) continue mainLoop;
        }
        uniquePercentages.push(vatValue);
    }
    const singlePercentage: VATValue | null = uniquePercentages.length === 1 ? uniquePercentages[0] : null;
    if (!singlePercentage) {
        countryCodes.sort((keyA: string, keyB: string) => {
            if (keyA === 'NL') return -1;
            else if (keyB === 'NL') return 1;
            return keyA.localeCompare(keyB);
        });
    }

    return (
        <div className='vat-percentage-cell'>
            {singlePercentage ? (
                <label className='country-vat'>
                    <AwesomeIcon name='globe' title={i18n.t('util.salesDashboard.allCountries', 'Alle landen')} />{' '}
                    <span className='vat-value'>{translateVatValue(singlePercentage)}</span>
                </label>
            ) : (
                <div className='countries-container'>
                    {countryCodes.map((country: string, index: number) => {
                        return (
                            <label key={index} className='country-vat'>
                                <CountryFlag countryCode={country} />{' '}
                                <span className='vat-value'>
                                    {translateVatValue(countryValues.getCountryValue(country)!)}
                                </span>
                            </label>
                        );
                    })}
                </div>
            )}
            <AwesomeIcon
                name='cog'
                className='cog-icon'
                onClick={onNavigate(`/instellingen/producten?ean=${rowData.ean}`)}
                title={i18n.t('util.salesDashboard.setVat', 'BTW instellen')}
            />
        </div>
    );
}

function renderNumber(field: keyof SalesDashboardProduct, dashIfZero: boolean = false) {
    return function (rowData: SalesDashboardProduct) {
        const rowValue = Number(rowData[field]);
        let value: string = rowValue.toString();
        if (!rowValue && dashIfZero) value = '-';
        return <span>{value}</span>;
    };
}

function renderPercentage(
    field: keyof SalesDashboardProduct,
    round: boolean = true,
    dashIfZero: boolean = false,
    infoIfZero?: string
) {
    return function (rowData: SalesDashboardProduct) {
        const rowValue = Number(rowData[field]);
        let value: string | number = rowValue ?? '0';
        if (!rowValue && dashIfZero && !rowData.has_product_costs) value = '-';
        else if (round) value = Number(value).toFixed(2) + '%';
        else value = value + '%';
        const showInfo = !!infoIfZero && !rowValue && !rowData.has_product_costs;
        return (
            <div className='cell-with-info'>
                <span>{value}</span>
                {showInfo && (
                    <div className='cell-info-container'>
                        <InfoIcon />
                        <p className='info-message'>{infoIfZero}</p>
                    </div>
                )}
            </div>
        );
    };
}

export type SalesFilter = {
    [key in keyof Omit<SalesDashboardProduct, 'vat_percentage'>]:
        DataTableFilterMetaData | DataTableOperatorFilterMetaData;
};
export const defaultSalesFilter: SalesFilter = {
    global_id: {operator: FilterOperator.AND, constraints: [{value: null, matchMode: FilterMatchMode.EQUALS}]},
    title: {operator: FilterOperator.AND, constraints: [{value: null, matchMode: FilterMatchMode.EQUALS}]},
    ean: {operator: FilterOperator.AND, constraints: [{value: null, matchMode: FilterMatchMode.EQUALS}]},
    image_url: {operator: FilterOperator.AND, constraints: [{value: null, matchMode: FilterMatchMode.EQUALS}]},
    commission: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    commission_percentage: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    avg_commission: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    avg_price: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    avg_price_incl: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    costs: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    margin: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    profit: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    profit_per_product: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    quantity: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    num_orders: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    revenue: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    revenue_incl: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    returned_quantity: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}]
    },
    has_product_costs: {
        operator: FilterOperator.AND,
        constraints: [{value: null, matchMode: FilterMatchMode.GREATER_THAN}]
    }
};

const LVBSizeConstraints: Record<LVBSize, ProductDimensions> = {
    [LVBSize.XXXS]: {
        length_mm: 235,
        width_mm: 165,
        height_mm: 30,
        weight_mg: 1000000
    },
    [LVBSize.XXS]: {
        length_mm: 375,
        width_mm: 260,
        height_mm: 30,
        weight_mg: 2000000
    },
    [LVBSize.XS]: {
        length_mm: 375,
        width_mm: 260,
        height_mm: 50,
        weight_mg: 5000000
    },
    [LVBSize.S]: {
        length_mm: 450,
        width_mm: 300,
        height_mm: 80,
        weight_mg: 5000000
    },
    [LVBSize.M]: {
        length_mm: 550,
        width_mm: 350,
        height_mm: 200,
        weight_mg: 8000000
    },
    [LVBSize.L]: {
        length_mm: 720,
        width_mm: 500,
        height_mm: 410,
        weight_mg: 15000000,
        content_ml: 70000
    },
    [LVBSize.XL]: {
        length_mm: 1000,
        width_mm: 700,
        height_mm: 550,
        weight_mg: 30000000,
        content_ml: 385000
    },
    [LVBSize.XXL]: {
        length_mm: 1750,
        width_mm: 780,
        height_mm: 580,
        weight_mg: 31500000,
        content_ml: 791700
    },
    [LVBSize.XXXL_1]: {
        length_mm: 2400,
        width_mm: 1100,
        height_mm: Number.MAX_SAFE_INTEGER,
        belt_size_mm: 4500,
        weight_mg: 31500000,
        content_ml: 300000
    },
    [LVBSize.XXXL_2]: {
        length_mm: 4000,
        width_mm: 2000,
        height_mm: Number.MAX_SAFE_INTEGER,
        weight_mg: 63000000,
        content_ml: 3000000
    }
};

export function getLVBSizeFromDimensions(dimensions: ProductDimensions): LVBSize | null {
    const {length_mm, width_mm, height_mm, weight_mg} = dimensions;
    if (!length_mm || !width_mm || !height_mm) return null;
    const content_ml = (length_mm * width_mm * height_mm) / 1000;
    const belt_size_mm = length_mm + width_mm * 2 + height_mm * 2;
    for (const [size, dimension] of Object.entries<ProductDimensions>(LVBSizeConstraints)) {
        if (!checkLVBDimension(length_mm, dimension.length_mm)) continue;
        if (!checkLVBDimension(width_mm, dimension.width_mm)) continue;
        if (!checkLVBDimension(height_mm, dimension.height_mm)) continue;
        if (!checkLVBDimension(weight_mg, dimension.weight_mg)) continue;
        if (dimension.content_ml && !checkLVBDimension(content_ml, dimension.content_ml)) continue;
        if (dimension.belt_size_mm && !checkLVBDimension(belt_size_mm, dimension.belt_size_mm)) continue;
        return size as LVBSize;
    }
    return null;
}

function checkLVBDimension(productDimension: number | null, checkDimension: number | null): boolean {
    if (!checkDimension || !productDimension) return true;
    return checkDimension >= productDimension;
}

export const salesDashboardColumnMap: Record<SalesCategory, Array<SalesColumn>> = {
    [SalesCategory.SALES]: [SalesColumn.QUANTITY, SalesColumn.REVENUE, SalesColumn.ORGANIC_SALES],
    [SalesCategory.PROFIT]: [SalesColumn.PROFIT, SalesColumn.MARGIN],
    [SalesCategory.COSTS]: [SalesColumn.PROD_COSTS, SalesColumn.COMMISSION, SalesColumn.VAT],
    [SalesCategory.ADVERTISING]: [SalesColumn.AD_SPENT, SalesColumn.SPONSORED_SALES, SalesColumn.ACOS, SalesColumn.CPC],
    [SalesCategory.RETURNS]: [
        SalesColumn.RETURN_QUANTITY,
        SalesColumn.RETURN_REVENUE,
        SalesColumn.RETURN_COSTS,
        SalesColumn.RETURN_COMMISSION
    ],
    [SalesCategory.STOCK]: [SalesColumn.STOCK],
    [SalesCategory.COMPARISONS]: []
};

export const allSalesDashboardColumns = Object.values(salesDashboardColumnMap).reduce(
    (prev: Array<SalesColumn>, current: Array<SalesColumn>) => prev.concat(current),
    []
);

export function salesCategoryIsVisible(
    category: SalesCategory,
    comparisonVisible: boolean,
    visibleColumns: Set<SalesColumn>
): boolean {
    if (category === SalesCategory.COMPARISONS) return comparisonVisible;
    const columns = salesDashboardColumnMap[category];
    return columns.some((column: SalesColumn) => visibleColumns.has(column));
}
